import Label from "components/Label";
import { useViewportScroll } from "framer-motion";
import { throttle } from "lodash";
import React, {
  FC,
  useCallback,
  useLayoutEffect,
  useRef,
  useState
} from "react";
import useComponentDimensionsGen from "hooks/use-component-dimensions-gen";
import useScreenSize from "hooks/use-screen-size";
import PhoneFrame from "../../../../../public/phone-frame.svg";
import App from "../../../../../public/app.png";

import styles from "./styles.module.scss";
import { personalizations } from "./constants";

const PersonalizedHomecare: FC = () => {
  const { scrollY } = useViewportScroll();
  const { measure, height: imageHeight } =
    useComponentDimensionsGen<HTMLImageElement>();
  const { isResponsive } = useScreenSize();

  const ref = useRef<HTMLDivElement>(null);

  const [attributes, setAttributes] = useState({
    top: 0,
    left: 0,
    width: 0,
    height: 0
  });

  const measureCallback = useCallback(
    throttle((el: HTMLElement) => {
      if (el) {
        const top = el.offsetTop;
        const left = el.offsetLeft;
        const width = el.clientWidth;
        const height = el.clientHeight;
        if (top || left) setAttributes({ top, left, height, width });
      }
    }, 0),
    []
  );

  useLayoutEffect(() => {
    const measureCurrentNode = () => {
      if (ref.current) measureCallback(ref.current);
    };

    window.addEventListener("scroll", measureCurrentNode);

    return () => window.removeEventListener("scroll", measureCurrentNode);
  }, [ref.current]);

  const innerScroll = scrollY.get() - attributes.top;
  const progress = innerScroll / attributes.height;

  const appStyles =
    attributes.height && innerScroll && imageHeight >= 0
      ? isResponsive
        ? {
            transform: `translate(0, -${Math.min(
              progress * imageHeight,
              imageHeight - 450
            )}px)`
          }
        : {
            transform: `translate(0, -${Math.min(
              progress * imageHeight,
              imageHeight - 625
            )}px)`
          }
      : {};

  const isInBounds = (index: number, scale = 1) => {
    if (index)
      return (
        progress / scale > index / personalizations.length &&
        progress / scale < (index + 1) / personalizations.length
      );

    return progress / scale < (index + 1) / personalizations.length;
  };

  return (
    <div ref={ref} className={styles.container} style={{ height: 1923 }}>
      <div className={styles.content}>
        <div className={styles.headings}>
          <Label className={styles.title} variant="h3" color="white">
            Personalized homecare, made just for you
          </Label>
          <Label className={styles.subtitle} variant="s2" color="white">
            Our app is your single point of all contact for all projects — big
            and small.
          </Label>
        </div>
        <div className={styles.frameWrapper}>
          <PhoneFrame className={styles.frame} />
          <div className={styles.appWrapper}>
            <img
              ref={measure}
              style={appStyles}
              alt=""
              src={App.src}
              className={styles.app}
            />
          </div>
        </div>
        <div className={styles.personalizations}>
          {personalizations.map((personalization, index) => (
            <div className={styles.personalization}>
              <div
                className={`${styles.separator} ${
                  isInBounds(index, 0.6) && styles.selectedSeparator
                }`}
              />
              <Label
                className={`${isInBounds(index, 0.6) && styles.selectedLabel} ${
                  styles.personalizationLabel
                }`}
                color="white"
              >
                {personalization}
              </Label>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PersonalizedHomecare;
